<script setup lang="ts">
import {computed, watch} from "vue";

const props = defineProps({
    items: Array,
    theme: Object,
    roundness: String,
})

const form = defineModel('form');

const currentItem = computed(() => props.items.find(el => el.id === form.value.id_rental_items)?.additional_costs)

function formatCurrency(value: number, currency: string): string {
    return new Intl.NumberFormat('cs-CZ', {style: 'currency', currency}).format(value);
}

const addRemoveCost = (costId: number, isOptional: boolean = false) => {
    if (isOptional) {
        const costIndex = form.value.additional_costs.findIndex(a_cost => a_cost === costId);
        if (costIndex === -1) {
            form.value.additional_costs.push(costId);
        } else {
            form.value.additional_costs.splice(costIndex, 1);
        }
    }
}

watch(() => form.value.id_rental_items, () => {
    form.value.additional_costs = currentItem.value?.filter(cost => !cost.is_optional)?.flatMap(cost => [cost.id]) ?? [];
}, {immediate: true})
</script>

<template>
    <div class="nd-fset">
        <h4>{{ $t('widget.additional_costs') }}</h4>
        <div class="nd-extras">
            <div
                v-for="cost in currentItem"
                :key="cost.id"
                class="nd-extra"
                :class="{
                    'nd-extra-on': form.additional_costs?.includes(cost.id),
                    'nd-extra-opt': cost.is_optional,
                    'nd-extra-mand': !cost.is_optional,
                }"
                @click="addRemoveCost(cost.id, cost.is_optional)"
            >
                <div class="nd-extra-col">
                    <b class="nd-extra-name">{{ cost.name }}</b>
                    <span v-if="cost.description" class="nd-extra-desc">{{ cost.description }}</span>
                    <div class="nd-extra-price">
                        {{ formatCurrency(Number(cost.unit_price), cost.currency) }}
                        <span v-if="cost.vat_percentage > 0">&nbsp;· {{ cost.vat_percentage }}% DPH</span>
                    </div>
                </div>
                <div class="nd-pick">
                    <svg v-if="form.additional_costs?.includes(cost.id)" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="12" height="12">
                        <path d="M2 7l3.5 3.5L12 2.5"/>
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.nd-fset {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 0px 16px;
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
}

.nd-fset h4 {
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
    color: var(--rw-ink, #0F1115);
}

.nd-extras {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.nd-extra {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    border: 1px solid var(--rw-line, #E5E2DC);
    border-radius: 12px;
    padding: 14px;
    background: var(--rw-panel-bg, var(--rw-paper, #fff));
    transition: border-color 0.12s, background 0.12s;
}

.nd-extra.nd-extra-opt {
    cursor: pointer;
}

.nd-extra.nd-extra-opt:hover {
    border-color: var(--rw-ink-3, #6B7280);
}

.nd-extra.nd-extra-on {
    border-color: var(--brand, #2A6FDB);
    background: var(--rw-brand-tint, #F1F5FC);
}

.nd-extra.nd-extra-mand {
    opacity: 0.72;
}

.nd-extra-col {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.nd-extra-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--rw-ink, #0F1115);
}

.nd-extra-desc {
    font-size: 12px;
    color: var(--rw-ink-3, #6B7280);
}

.nd-extra-price {
    font-size: 12px;
    color: var(--rw-ink-2, #3A3F47);
    margin-top: 2px;
}

.nd-pick {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 1.5px solid var(--rw-line, #E5E2DC);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
    transition: background 0.12s, border-color 0.12s;
}

.nd-extra.nd-extra-on .nd-pick {
    background: var(--brand, #2A6FDB);
    border-color: var(--brand, #2A6FDB);
}
</style>
